// m002-frontend/src/pages/events-manager/TimelineSegmentActions.js
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, DatePicker, Input, Modal, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";

const { Text } = Typography;
const { TextArea } = Input;

// Default ordering for timeline tables: active segments (no end date) first,
// then closed segments newest-start-first. AntD's column sorter takes over
// on click; clearing the sort (3rd click) reverts to this order since it's
// the array order passed as dataSource.
export function sortTimelineRows(rows) {
  return [...rows].sort((a, b) => {
    const aActive = !a.union_end_datetime;
    const bActive = !b.union_end_datetime;
    if (aActive !== bActive) return aActive ? -1 : 1;
    return new Date(b.union_start_datetime) - new Date(a.union_start_datetime);
  });
}

// Editar/Excluir actions for one row of a zone-link timeline (Zonas e
// Colecoes, Zonas e Modulos). Shared so both screens show the same past-
// period warning, overlap error handling, and reason capture.
export default function TimelineSegmentActions({
  record,
  idField,
  entityLabel,
  onEdit,
  onDelete,
  onDone,
  messageApi,
}) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editStart, setEditStart] = useState(null);
  const [editEnd, setEditEnd] = useState(null);
  const [reason, setReason] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const id = record[idField];
  const touchesPast =
    !record.union_end_datetime ||
    dayjs(record.union_start_datetime).isBefore(dayjs().endOf("day"));

  const openEdit = () => {
    setEditStart(dayjs(record.union_start_datetime));
    setEditEnd(
      record.union_end_datetime ? dayjs(record.union_end_datetime) : null
    );
    setReason("");
    setErrorMsg("");
    setIsEditOpen(true);
  };

  const submitEdit = async () => {
    setIsSaving(true);
    setErrorMsg("");
    try {
      await onEdit(id, {
        union_start_datetime: editStart.format("YYYY-MM-DDTHH:mm:ss"),
        union_end_datetime: editEnd
          ? editEnd.format("YYYY-MM-DDTHH:mm:ss")
          : null,
        reason,
      });
      messageApi.success("Período atualizado com sucesso!");
      setIsEditOpen(false);
      onDone();
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.msg ||
          error?.response?.data?.detail ||
          "Não foi possível salvar as alterações."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const submitDelete = async () => {
    setIsSaving(true);
    setErrorMsg("");
    try {
      await onDelete(id, reason);
      messageApi.success("Período excluído com sucesso!");
      setIsDeleteOpen(false);
      onDone();
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.msg ||
          error?.response?.data?.detail ||
          "Não foi possível excluir este período."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Space>
        <Button icon={<EditOutlined />} onClick={openEdit} />
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => {
            setReason("");
            setErrorMsg("");
            setIsDeleteOpen(true);
          }}
        />
      </Space>

      <Modal
        open={isEditOpen}
        onCancel={() => setIsEditOpen(false)}
        title={`Editar período (${entityLabel})`}
        footer={null}>
        {touchesPast && (
          <Text type="warning" style={{ display: "block", marginBottom: 12 }}>
            Este período já ocorreu ou está em andamento. Alterar as datas vai
            recalcular os KPIs e gráficos desse período na próxima visualização.
          </Text>
        )}
        <Text strong>Início</Text>
        <div style={{ margin: "8px 0 16px" }}>
          <DatePicker
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
            value={editStart}
            onChange={setEditStart}
          />
        </div>
        <Text strong>Fim (deixe em branco para manter ativo)</Text>
        <div style={{ margin: "8px 0 16px" }}>
          <DatePicker
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
            value={editEnd}
            onChange={setEditEnd}
            allowClear
          />
        </div>
        <Text strong>Motivo (opcional)</Text>
        <TextArea
          style={{ margin: "8px 0 16px" }}
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {errorMsg && (
          <Text type="danger" style={{ display: "block", marginBottom: 12 }}>
            {errorMsg}
          </Text>
        )}
        <div style={{ textAlign: "right" }}>
          <Button
            onClick={() => setIsEditOpen(false)}
            style={{ marginRight: 10 }}>
            Cancelar
          </Button>
          <Button
            type="primary"
            loading={isSaving}
            disabled={!editStart}
            onClick={submitEdit}>
            Salvar
          </Button>
        </div>
      </Modal>

      <Modal
        open={isDeleteOpen}
        onCancel={() => setIsDeleteOpen(false)}
        title={`Excluir período (${entityLabel})`}
        footer={null}>
        <Text>
          Tem certeza que deseja excluir este período? Esta ação não pode ser
          desfeita.
        </Text>
        {touchesPast && (
          <Text type="warning" style={{ display: "block", marginTop: 12 }}>
            Este período já ocorreu ou está em andamento. Excluí-lo vai
            recalcular os KPIs e gráficos desse período na próxima visualização.
          </Text>
        )}
        <Text strong style={{ display: "block", marginTop: 16 }}>
          Motivo (opcional)
        </Text>
        <TextArea
          style={{ margin: "8px 0 16px" }}
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {errorMsg && (
          <Text type="danger" style={{ display: "block", marginBottom: 12 }}>
            {errorMsg}
          </Text>
        )}
        <div style={{ textAlign: "right" }}>
          <Button
            onClick={() => setIsDeleteOpen(false)}
            style={{ marginRight: 10 }}>
            Cancelar
          </Button>
          <Button danger loading={isSaving} onClick={submitDelete}>
            Excluir
          </Button>
        </div>
      </Modal>
    </>
  );
}
